import { compose } from '@descope-ui/common/utils';
import { getComponentName } from '@descope-ui/common/components-helpers';
import {
  createStyleMixin,
  draggableMixin,
  createProxy,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import { IconClass } from '@descope-ui/descope-icon/class';
import { clickableMixin } from './clickableMixin';

export const componentName = getComponentName('button');

const resetStyles = `
	:host {
		display: inline-block;
		box-sizing: border-box;
	}
	vaadin-button::before,
	vaadin-button::after {
		opacity: 0;
	}
	vaadin-button {
		margin: 0;
		min-width: 0;
		width: 100%;
		height: auto;
		box-shadow: none;
	}
	vaadin-button::part(label) {
		padding: 0;
    width: 100%;
	}
	vaadin-button::part(prefix) {
		margin-left: 0;
		margin-right: 0;
	}
`;

const iconStyles = `
	vaadin-button::part(prefix),
	vaadin-button::part(label) {
		display: flex;
		align-items: center;
	}
`;

const editorOverrides = `vaadin-button::part(label) { pointer-events: none; }`;

const { host, label, slottedIcon } = {
  host: { selector: () => ':host' },
  label: { selector: '::part(label)' },
  slottedIcon: { selector: () => '::slotted(descope-icon)' },
};

let loadingIndicatorStyles;

export const ButtonClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { property: 'width' },
      hostHeight: { property: 'height' },
      hostDirection: { ...host, property: 'direction' },
      fontSize: {},
      fontFamily: {},

      cursor: {},
      backgroundColor: {},

      outlineOffset: {},
      outlineColor: {},
      outlineStyle: {},
      outlineWidth: {},

      borderRadius: {},
      borderColor: {},
      borderStyle: {},
      borderWidth: {},

      verticalPadding: [{ property: 'padding-top' }, { property: 'padding-bottom' }],
      horizontalPadding: [
        { property: 'padding-right', fallback: '0.875em' },
        { property: 'padding-left', fallback: '0.875em' },
      ],

      labelTextColor: { property: 'color' },
      iconColor: {
		selector: () => `::slotted(*)`,
		property: IconClass.cssVarList.fill
	  },
      labelTextDecoration: { ...label, property: 'text-decoration' },
      labelSpacing: { ...label, property: 'gap' },
      textAlign: { ...label, property: 'justify-content', fallback: 'center' },

      iconSize: [
        { ...slottedIcon, property: 'width' },
        { ...slottedIcon, property: 'height' },
      ],
    },
  }),
  clickableMixin,
  draggableMixin,
  componentNameValidationMixin
)(
  createProxy({
    slots: ['', 'prefix', 'label', 'suffix'],
    wrappedEleName: 'vaadin-button',
    style: () => `
			${resetStyles}
			${iconStyles}
			${loadingIndicatorStyles}
			${editorOverrides}
			:host {
				padding: calc(var(${ButtonClass.cssVarList.outlineWidth}) + var(${ButtonClass.cssVarList.outlineOffset}));
			}
      :host([full-width="true"]) {
        width: var(${ButtonClass.cssVarList.hostWidth});
      }
			vaadin-button {
				height: calc(var(${ButtonClass.cssVarList.hostHeight}) - var(${ButtonClass.cssVarList.outlineWidth}) - var(${ButtonClass.cssVarList.outlineOffset}));
			}
			[square="true"]:not([full-width="true"]) {
				width: calc(var(${ButtonClass.cssVarList.hostWidth}) - var(${ButtonClass.cssVarList.outlineWidth}) - var(${ButtonClass.cssVarList.outlineOffset}));
        padding: 0;
			}
		`,
    excludeAttrsSync: ['tabindex', 'class', 'style'],
    componentName,
  })
);

const { color, fontSize } = ButtonClass.cssVarList;
loadingIndicatorStyles = `
	@keyframes spin {
		0% { -webkit-transform: rotate(0deg); }
		100% { -webkit-transform: rotate(360deg); }
	}
	:host([loading="true"]) ::before {
		animation: spin 2s linear infinite;
		position: absolute;
		content: '';
		z-index: 1;
		box-sizing: border-box;
		border-radius: 50%;
		border-bottom-color: transparent;
		border-left-color: transparent;
		border-style: solid;
		color: var(${color});
		top: calc(50% - (var(${fontSize}) / 2));
		left: calc(50% - (var(${fontSize}) / 2));
		border-width: calc(var(${fontSize}) / 10);
		width: var(${fontSize});
		height: var(${fontSize});
	}
	:host([disabled="true"]),
	:host([loading="true"]) {
		pointer-events: none;
	}
	:host([loading="true"]) ::part(prefix),
	:host([loading="true"]) ::part(label) {
		visibility: hidden;
	}
`;
